import type { Proposal } from '@/apis/extended/proposalApi';
import type { ContractClauseComparison } from '@/components/Admin/ContractComparisonTable';
import {
  extractComprehensiveContractClausesWithIds,
  type ComprehensiveClauseCategory,
  type ComprehensiveClauseData,
} from '@/utils/comprehensiveClauseMapping';
import type { Contract } from '@/types/ContractType';

function ts(p: Proposal): number {
  const d = p.updatedAt || p.createdAt;
  return d ? new Date(d).getTime() : 0;
}

function proposalsForClause(
  proposals: Proposal[],
  clauseId: string
): Proposal[] {
  return proposals
    .filter((p) => p.clauseId === clauseId)
    .sort((a, b) => ts(b) - ts(a));
}

function latestByRole(
  proposals: Proposal[],
  clauseId: string,
  role: Proposal['proposedBy']
): Proposal | undefined {
  return proposalsForClause(proposals, clauseId).find(
    (p) => p.proposedBy === role
  );
}

function mapTableCategory(catId: string): ContractClauseComparison['category'] {
  if (catId === 'price') return 'offer';
  if (catId === 'timeline') return 'timeline';
  if (catId === 'contingency' || catId === 'inspection') return 'contingency';
  if (catId === 'occupancy') return 'timeline';
  if (catId === 'property') return 'property';
  if (catId === 'legal') return 'other';
  return 'other';
}

function mapRowStatus(
  clause: ComprehensiveClauseData,
  clauseProposals: Proposal[]
): ContractClauseComparison['status'] {
  const latest = clauseProposals[0];
  if (latest) {
    const s = latest.status;
    if (s === 'accepted') return 'agreed';
    if (s === 'rejected' || s === 'expired') return 'disputed';
    if (s === 'countered') return 'modified';
    if (s === 'pending') return 'pending';
  }
  const n = clause.negotiationStatus;
  if (n === 'accepted') return 'agreed';
  if (n === 'rejected') return 'disputed';
  if (n === 'countered') return 'modified';
  if (n === 'original') return 'agreed';
  return 'agreed';
}

function mapLastUpdatedBy(
  clauseProposals: Proposal[]
): ContractClauseComparison['lastUpdatedBy'] {
  const latest = clauseProposals[0];
  if (latest?.proposedBy === 'buyer') return 'buyer';
  if (latest?.proposedBy === 'seller') return 'seller';
  if (latest?.proposedBy === 'agent') return 'agent';
  return 'agent';
}

function buildNegotiationHistory(
  clauseProposals: Proposal[]
): ContractClauseComparison['negotiationHistory'] {
  return [...clauseProposals]
    .sort((a, b) => ts(a) - ts(b))
    .map((p) => ({
      party: p.proposedBy,
      value: p.proposedValue,
      timestamp: new Date(p.createdAt),
      notes: [p.message, p.status && `Status: ${p.status}`]
        .filter(Boolean)
        .join(' · '),
    }));
}

function toComparisonRow(
  category: ComprehensiveClauseCategory,
  clause: ComprehensiveClauseData,
  proposals: Proposal[]
): ContractClauseComparison {
  const clauseProposals = proposalsForClause(proposals, clause.id);
  const lastBuyer = latestByRole(proposals, clause.id, 'buyer');
  const lastSeller = latestByRole(proposals, clause.id, 'seller');
  const lastAgent = latestByRole(proposals, clause.id, 'agent');

  const effective = clause.value;

  const buyerInput = lastBuyer?.proposedValue ?? effective;
  const sellerInput = lastSeller?.proposedValue ?? effective;
  const agentInput = lastAgent?.proposedValue ?? effective;

  const notesParts = [
    clause.description,
    clause.source && `Source: ${clause.source}`,
  ].filter(Boolean);

  const latest = clauseProposals[0];
  if (latest?.message) notesParts.push(`Latest note: ${latest.message}`);

  return {
    clauseName: clause.title,
    category: mapTableCategory(category.id),
    sellerInput,
    agentInput,
    buyerInput,
    adminNotes: notesParts.join(' · '),
    status: mapRowStatus(clause, clauseProposals),
    lastUpdatedBy: mapLastUpdatedBy(clauseProposals),
    lastUpdatedAt: latest
      ? new Date(latest.updatedAt || latest.createdAt)
      : new Date(),
    negotiationHistory: buildNegotiationHistory(clauseProposals),
  };
}

/**
 * Full clause grid for admin property dashboard: same Clause Library as negotiation flows,
 * with per-party columns derived from proposals where available.
 */
/** Populated `buyer` user from contract GET; not on base `Contract` type. */
type ContractWithPopulatedBuyer = Contract & {
  buyer?: {
    loanInfo?: {
      monthlyIncome?: number;
      creditScore?: number;
      monthlyDebt?: number;
    };
  };
};

function appendBuyerFinancialSnapshot(
  contract: Contract,
  rows: ContractClauseComparison[]
) {
  const { buyer } = contract as ContractWithPopulatedBuyer;
  const income = buyer?.loanInfo?.monthlyIncome;
  const credit = buyer?.loanInfo?.creditScore;
  const debt = buyer?.loanInfo?.monthlyDebt;
  const baseDate = new Date(contract.updatedAt || contract.createdAt);

  const push = (
    clauseName: string,
    val: string | number | null | undefined
  ) => {
    if (val == null || val === '') return;
    const v = val as string | number;
    rows.push({
      clauseName,
      category: 'other',
      sellerInput: v,
      agentInput: v,
      buyerInput: v,
      adminNotes: 'Buyer qualification (URLA)',
      status: 'agreed',
      lastUpdatedBy: 'buyer',
      lastUpdatedAt: baseDate,
      negotiationHistory: [],
    });
  };

  push('Buyer Monthly Income', income);
  push('Buyer Credit Score', credit);
  push('Buyer Monthly Debt', debt);
}

export function buildAdminContractComparisonRows(
  contract: Contract,
  contractId: string,
  proposals: Proposal[]
): ContractClauseComparison[] {
  if (!contract || !contractId) return [];

  const merged: Proposal[] = Array.isArray(proposals) ? proposals : [];
  const categories = extractComprehensiveContractClausesWithIds(
    contract,
    contractId
  );

  const rows: ContractClauseComparison[] = [];
  for (const category of categories) {
    for (const clause of category.clauses) {
      rows.push(toComparisonRow(category, clause, merged));
    }
  }
  appendBuyerFinancialSnapshot(contract, rows);
  return rows;
}
